import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { FaLeaf, FaApple } from 'react-icons/fa'
import { FcGoogle } from 'react-icons/fc'
import { FiEye, FiEyeOff, FiCheckCircle } from 'react-icons/fi'
import { mergeGuestCart } from '../../feature/cartSlice'
import sideImg from '../../assets/40883aa1-a1d5-4c5c-ab2e-89acf9b1646e.png'

const inputCls =
  'w-full h-[44px] border border-[#dfe7d9] bg-white px-4 text-[13px] text-[#1f2d22] rounded-md outline-none focus:border-[#2f6b3a] transition-colors placeholder:text-[#9aa79c]'

const OTP_LENGTH = 6
const RESEND_SECONDS = 30

const isEmail = (v) => /^\S+@\S+\.\S+$/.test(v)
const isPhone = (v) => /^(\+91)?[\s-]?[6-9]\d{9}$/.test(v.replace(/\s|-/g, '').replace(/^\+91/, '+91'))

/* POST json and always return the parsed body (also for 400 / 401 / 409 errors).
   `_ok` tells whether the HTTP status was a success. */
const post = async (url, body) => {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  return { ...data, _ok: res.ok }
}

const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  // 'password' | 'otp'   (OTP needs /users/send-otp and /users/verify-otp on the backend)
  const [mode, setMode] = useState('password')
  // OTP flow: 'request' (enter email/mobile) | 'verify' (enter code)
  const [otpStep, setOtpStep] = useState('request')

  const [formData, setformData] = useState({ email: '', password: '' })
  const [identifier, setIdentifier] = useState('')
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''))
  const otpRefs = useRef([])

  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [timer, setTimer] = useState(0)

  const baseurl = process.env.REACT_APP_BASE_API
  const postData = (body) => post(`${baseurl}/users/login`, body)
  const sendOtpApi = (body) => post(`${baseurl}/users/send-otp`, body)
  const verifyOtpApi = (body) => post(`${baseurl}/users/verify-otp`, body)

  /* ---------- resend countdown ---------- */
  useEffect(() => {
    if (timer <= 0) return
    const id = setInterval(() => setTimer((t) => t - 1), 1000)
    return () => clearInterval(id)
  }, [timer])

  /* ---------- helpers ---------- */
  const resetMessages = () => {
    setError('')
    setSuccess('')
  }

  const switchMode = (next) => {
    setMode(next)
    setOtpStep('request')
    setOtp(Array(OTP_LENGTH).fill(''))
    setTimer(0)
    resetMessages()
  }
          
    const finishLogin = async (response) => {
    localStorage.setItem('token', response.token)
    if (response.user) localStorage.setItem('user', JSON.stringify(response.user))
    setSuccess(response.message || 'Login successful')                             
           
    // move the guest cart into the user's cart, then load the saved cart
    await dispatch(mergeGuestCart())

    // admin -> admin panel, normal user -> same as before
    let role = response.user?.role
    if (!role) {
      // some responses (for example OTP login) may not include the role, so ask the server
      try {
        const me = await fetch(`${baseurl}/users/me`, {
          headers: { Authorization: `Bearer ${response.token}` },
        })
        role = (await me.json())?.role
      } catch (e) {
        /* ignore, treat as a normal user */
      }
    }

    if (String(role).toLowerCase() === 'admin') {
      navigate('/admin', { replace: true })
    } else {
      navigate(location.state?.from || '/myaccount/profile', { replace: true })
    }
  }

  /* ---------- password login ---------- */
  const handleChange = (e) => {
    setformData({ ...formData, [e.target.name]: e.target.value })
    setError('')
  }

  const handlePasswordLogin = async (e) => {
    e.preventDefault()
    resetMessages()

    if (!formData.email.trim() || !formData.password) {
      setError('Please enter your email and password.')
      return
    }

    try {
      setLoading(true)
      const response = await postData(formData)
      if (response?.token) await finishLogin(response)
      else setError(response?.message || 'Login failed. Please try again.')
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  /* ---------- OTP: send ---------- */
  const handleSendOtp = async (e) => {
    e?.preventDefault()
    resetMessages()

    const value = identifier.trim()
    if (!value) {
      setError('Please enter your mobile number or email.')
      return
    }
    if (!isEmail(value) && !isPhone(value)) {
      setError('Enter a valid 10-digit mobile number or email address.')
      return
    }

    try {
      setLoading(true)
      const response = await sendOtpApi({ identifier: value })
      if (response?.success === false || response?._ok === false) {
        setError(response?.message || 'Could not send OTP. Please try again.')
        return
      }
      setOtpStep('verify')
      setOtp(Array(OTP_LENGTH).fill(''))
      setTimer(RESEND_SECONDS)
      setSuccess(response?.message || `OTP sent to ${value}`)
      setTimeout(() => otpRefs.current[0]?.focus(), 50)
    } catch (err) {
      setError(err?.message || 'Could not send OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  /* ---------- OTP: boxes ---------- */
  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    const next = [...otp]
    next[index] = digit
    setOtp(next)
    setError('')
    if (digit && index < OTP_LENGTH - 1) otpRefs.current[index + 1]?.focus()
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }
    if (e.key === 'ArrowLeft' && index > 0) otpRefs.current[index - 1]?.focus()
    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) otpRefs.current[index + 1]?.focus()
  }

  const handleOtpPaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    const next = Array(OTP_LENGTH).fill('')
    pasted.split('').forEach((d, i) => (next[i] = d))
    setOtp(next)
    otpRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus()
  }

  /* ---------- OTP: verify ---------- */
  const handleVerifyOtp = async (e) => {
    e.preventDefault()
    resetMessages()

    const code = otp.join('')
    if (code.length !== OTP_LENGTH) {
      setError(`Please enter the ${OTP_LENGTH}-digit OTP.`)
      return
    }
    
    try {
      setLoading(true)
      const response = await verifyOtpApi({ identifier: identifier.trim(), otp: code })
      if (response?.token) await finishLogin(response)
      else setError(response?.message || 'Invalid OTP. Please try again.')
    } catch (err) {
      setError(err?.message || 'Invalid OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }
   
  const handleResend = () => {
    if (timer > 0 || loading) return
    handleSendOtp()
  }

  const changeIdentifier = () => {
    setOtpStep('request')
    setOtp(Array(OTP_LENGTH).fill(''))
    setTimer(0)
    resetMessages()
  }

  // Google / Apple login need their own setup on your backend
  const socialLogin = (provider) => {
    setSuccess('')
    setError(`${provider} login is not connected yet.`)
  }

  const primaryBtn =
    'w-full h-[46px] mt-1 bg-[#25663a] hover:bg-[#1a4a29] disabled:opacity-60 transition-colors text-white text-[14px] font-semibold rounded-full'

  const messages = (
    <>
      {error && (
        <p role="alert" className="text-[13px] text-red-500">
          {error}
        </p>
      )}
      {success && (
        <p role="status" className="flex items-center gap-2 text-[13px] text-[#1f4a2b]">
          <FiCheckCircle className="text-[#2f6b3a]" /> {success}
        </p>
      )}
    </>
  )   
      
  return (
    <div className="min-h-screen bg-[#eef2e8] flex items-center justify-center mt-[50px] px-4 py-8">
       <div className="w-full max-w-[940px] bg-[#f8faf3] border border-[#e3e9dd] shadow-xl rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-[1.6fr_1fr]">
        {/* ============ LEFT: FORM ============ */}
        <div className="px-6 sm:px-12 py-8 sm:py-10">
          {/* Logo */}

          <Link to="/" className="flex items-center justify-center gap-2.5 mb-6">
            <span className="w-[42px] h-[42px] rounded-full bg-white border border-[#cfe0cb] flex items-center justify-center">
              <FaLeaf className="text-[18px] text-[#2f6b3a]" />
            </span>
            <span className="text-left">
              <span className="block font-serif text-[26px] text-[#1f4a2b] leading-none">
                Jeetayu
              </span>
              <span className="block text-[7px] tracking-[1.6px] text-[#6b7a6d] mt-1">
                AYURVEDA FOR A BETTER LIFE
              </span>
            </span>
          </Link>

          <h2 className="font-serif font-bold text-[26px] sm:text-[30px] text-center text-[#1f4a2b] mb-1.5">
            Welcome Back!
          </h2>
          <p className="text-center text-[12px] text-[#4b574d] mb-6">
            Login to your account to continue your wellness journey.
          </p>

          {/* Mode switch */}
          <div
            role="tablist"
            aria-label="Login method"
            className="grid grid-cols-2 gap-1 p-1 mb-6 bg-[#e9efe2] rounded-full"
          >
            {[
              { key: 'otp', label: 'Login with OTP' },
              { key: 'password', label: 'Login with Password' },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={mode === t.key}
                onClick={() => switchMode(t.key)}
                className={`h-[36px] rounded-full text-[12px] font-medium transition-colors ${
                  mode === t.key
                    ? 'bg-white text-[#1f4a2b] shadow-sm'
                    : 'text-[#6b7a6d] hover:text-[#1f4a2b]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* ---------- PASSWORD MODE ---------- */}
          {mode === 'password' && (
            <form className="space-y-4" onSubmit={handlePasswordLogin} noValidate>
              <div>
                <label htmlFor="email" className="block mb-1.5 text-[11px] text-[#6b7a6d]">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  placeholder="Enter your email"
                  className={inputCls}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password" className="block text-[11px] text-[#6b7a6d]">
                    Password
                  </label>
                  <Link to="/forgot-password" className="text-[11px] text-[#2f6b3a] hover:underline">
                    Forgot Password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className={`${inputCls} pr-11`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b7a6d] hover:text-[#1f4a2b]"
                  >
                    {showPassword ? <FiEyeOff className="text-[16px]" /> : <FiEye className="text-[16px]" />}
                  </button>
                </div>
              </div>
               
              {messages}
                        
              <button type="submit" disabled={loading} className={primaryBtn}>
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          )}

          {/* ---------- OTP MODE: step 1 (request) ---------- */}
          {mode === 'otp' && otpStep === 'request' && (
            <form className="space-y-4" onSubmit={handleSendOtp} noValidate>
              <div>
                <label htmlFor="identifier" className="block mb-1.5 text-[11px] text-[#6b7a6d]">
                  Mobile Number / Email
                </label>
                <input
                  id="identifier"
                  type="text"
                  inputMode="email"
                  value={identifier}
                  onChange={(e) => {
                    setIdentifier(e.target.value)
                    setError('')
                  }}
                  autoComplete="username"
                  placeholder="+91 98765 43210 or you@example.com"
                  className={inputCls}
                />
              </div>

              {messages}

              <button type="submit" disabled={loading} className={primaryBtn}>
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </button>
            </form>
          )}

          {/* ---------- OTP MODE: step 2 (verify) ---------- */}
          {mode === 'otp' && otpStep === 'verify' && (
            <form className="space-y-4" onSubmit={handleVerifyOtp} noValidate>
              <p className="text-center text-[12px] text-[#4b574d]">
                Enter the {OTP_LENGTH}-digit code sent to{' '}
                <span className="font-semibold text-[#1f4a2b]">{identifier}</span>{' '}
                <button
                  type="button"
                  onClick={changeIdentifier}
                  className="text-[#25663a] hover:underline"
                >
                  Change
                </button>
              </p>

              <div className="flex justify-center gap-2 sm:gap-3" onPaste={handleOtpPaste}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    autoComplete={i === 0 ? 'one-time-code' : 'off'}
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    onFocus={(e) => e.target.select()}
                    aria-label={`OTP digit ${i + 1}`}
                    className="w-[42px] h-[48px] sm:w-[46px] sm:h-[52px] text-center text-[18px] font-semibold text-[#1f4a2b] bg-white border border-[#dfe7d9] rounded-md outline-none focus:border-[#2f6b3a] transition-colors"
                  />
                ))}
              </div>

              <div className="text-center">{messages}</div>

              <button type="submit" disabled={loading} className={primaryBtn}>
                {loading ? 'Verifying...' : 'Verify & Login'}
              </button>

              <p className="text-center text-[12px] text-[#6b7a6d]">
                Didn't get the code?{' '}
                {timer > 0 ? (
                  <span>Resend in {timer}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={loading}
                    className="text-[#25663a] font-semibold hover:underline disabled:opacity-60"
                  >
                    Resend OTP
                  </button>
                )}
              </p>
            </form>
          )}

          <p className="text-center text-[11px] text-[#6b7a6d] my-5">Or continue with</p>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => socialLogin('Google')}
              className="h-[42px] flex items-center justify-center gap-2 bg-white border border-[#dfe7d9] rounded-md text-[12px] font-medium text-[#1f2d22] hover:bg-[#f2f6ec] transition-colors"
            >
              <FcGoogle className="text-[17px]" /> Google
            </button>
            <button
              type="button"
              onClick={() => socialLogin('Apple')}
              className="h-[42px] flex items-center justify-center gap-2 bg-white border border-[#dfe7d9] rounded-md text-[12px] font-medium text-[#1f2d22] hover:bg-[#f2f6ec] transition-colors"
            >
              <FaApple className="text-[17px] text-black" /> Apple
            </button>
          </div>
               
          <p className="text-center text-[11px] text-[#4b574d] mt-6">
            New to Jeetayu?{' '}
            <Link to="/signup" className="text-[#25663a] font-semibold hover:underline">
              Create an Account
            </Link>
          </p>
        </div>

        {/* ============ RIGHT: IMAGE PANEL ============ */}
        <div className="relative hidden md:block min-h-[560px] bg-[#f1f4e8] overflow-hidden">
          <img
            src={sideImg}
            alt=""
            aria-hidden="true"
            className="absolute bottom-0 left-0 w-full h-[68%] object-cover object-center"
            style={{
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, #000 30%)',
              maskImage: 'linear-gradient(to bottom, transparent 0%, #000 30%)',
            }}
          />
          
          <FaLeaf className="absolute top-3 left-3 text-[34px] text-[#9fc58f]/70 -rotate-45" />
          <FaLeaf className="absolute top-2 right-3 text-[40px] text-[#8fba7e]/70 rotate-45" />

          <div className="relative flex justify-center pt-20 px-6">
            {/* <p className="font-serif text-[28px] lg:text-[30px] leading-[1.25] text-[#1f4a2b] text-center">
              Good things
              <br />
              take care
              <br />
              of you.
            </p> */}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
